/**
 * Cova Chat — call persistence models (Model.Class). Same modifier rules as
 * chat.model.ts: server-set-once → DateTimeInsert, mutable → DateTimeUpdate,
 * nullable business timestamps → plain S.NullOr(S.DateTimeUtc). Server-owned
 * identity columns use FieldExcept so RPC input can't forge them.
 *
 * Frontend NEVER imports this file — it consumes the RPC view shapes.
 *
 * Media/signaling have NO model here: AWS Chime hosts the call's media and supplies
 * ICE/TURN; SDP/ICE are never seen, let alone persisted.
 */
import { Schema as S } from "effect"
import { Model } from "@effect/sql"
import { CallId, ConversationId, CallStatus, MeetingId } from "./chat-enums"

// ============================================================================
// chat_calls — one row per call; lifecycle status written by the call RPCs
// ============================================================================
export class ChatCall extends Model.Class<ChatCall>("ChatCall")({
  id: Model.Generated(CallId),
  conversationId: ConversationId,
  organizationId: S.String.pipe(Model.FieldExcept("jsonCreate", "jsonUpdate")), // initiator org; audit anchor, NOT the access key
  initiatorUserId: S.String.pipe(Model.FieldExcept("jsonCreate", "jsonUpdate")),
  status: CallStatus,
  /** The Chime meeting carrying this call's media + live transcript. Server-owned
   *  (set via setCallMeeting when the room opens at call start, BEFORE ringing), so
   *  it's excluded from client-writable variants like organizationId/initiatorUserId.
   *  A ringing/declined/missed call already has one; NULL only until the room opens
   *  — a call that fails before its room opens stays NULL. */
  meetingId: S.NullOr(MeetingId).pipe(Model.FieldExcept("jsonCreate", "jsonUpdate")),
  endReason: S.NullOr(S.String),
  startedAt: Model.DateTimeInsert,
  connectedAt: S.NullOr(S.DateTimeUtc), // nullable business ts → plain
  endedAt: S.NullOr(S.DateTimeUtc),
  createdAt: Model.DateTimeInsert,
  updatedAt: Model.DateTimeUpdate, // DB trigger keeps this fresh (v4.38)
}) {}
export type ChatCallJson = typeof ChatCall.json.Type

/** Whether the viewer placed the call (`outgoing`) or received it (`incoming`).
 *  Computed per-viewer server-side from `initiator_user_id`. */
export const CallDirection = S.Literal("outgoing", "incoming")
export type CallDirection = typeof CallDirection.Type

/**
 * A single call-history row for the conversation "Calls" tab (spec: call logs).
 * A viewer-scoped projection of `chat_calls` — `direction` is relative to the
 * caller and `durationSeconds` is derived (connected→ended), so this is a
 * response DTO, NOT a persisted model. `hasTranscript` is always `false` until
 * the recording slice lands; the field is on the wire now so the panel's
 * transcript affordance needs no contract change in Phase 2.
 */
export const CallLogView = S.Struct({
  id: CallId,
  direction: CallDirection,
  status: CallStatus,
  startedAt: S.DateTimeUtc,
  durationSeconds: S.NullOr(S.Number),
  endReason: S.NullOr(S.String),
  hasTranscript: S.Boolean,
})
export type CallLogView = typeof CallLogView.Type

// ============================================================================
// chat_call_recordings — opt-in recording + transcript, owned by chat context
